import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './StatsPage.css';
import coffeeIcon from './assets/icons/bmc-logo-yellow.png';
import logo from './assets/icons/logo-no-background.png';
import facebookIcon from './assets/icons/facebook-brands-solid.svg';
import xIcon from './assets/icons/x-twitter-brands-solid.svg';
import linkIcon from './assets/icons/copy-regular.svg';
import triviaIcon from './assets/icons/masks-theater-solid.svg';
import statsIcon from './assets/icons/chart-simple-solid.svg';
import SideMenu from './SideMenu';
import FooterLinks from './FooterLinks';

const StatsPage = ({
	stats,
	handleTwitterShare,
	handleFacebookShare,
	handleCopyLink,
	copied,
}) => {
	const navigate = useNavigate();

	const chartOptions = {
		chart: {
			type: 'bar',
			toolbar: {
				show: false, // Hides the toolbar options
			},
			animations: {
				enabled: true,
				easing: 'easeinout',
				speed: 800,
			},
			margin: {
				top: 0, // Adjust the top margin
				right: 0,
				bottom: 0,
				left: 0,
			},
			padding: {
				top: 0, // Adjust the top margin
				right: 0,
				bottom: 0,
				left: 0,
			},
		},
		plotOptions: {
			bar: {
				horizontal: true, // Vertical bars
				barHeight: '90%', // Adjust the bar width
				borderRadius: 4, // Rounded corners
			},
			dataLabels: {
				position: 'center', // Ensure data labels are centered
			},
		},
		xaxis: {
			categories: ['1', '2', '3', '4', '5', '6'],
			title: {
				style: {
					fontSize: '20px',
					fontWeight: 'bold',
				},
			},
			labels: {
				show: false, // Hide x-axis labels (numbers or text)
			},
			axisBorder: {
				show: false, // Remove the x-axis border
			},
			axisTicks: {
				show: false, // Remove x-axis ticks
			},
		},
		yaxis: {
			title: {
				style: {
					fontSize: '14px',
					fontWeight: 'bold',
				},
			},
			labels: {
				style: {
					fontSize: '14px',
					fontWeight: 'bold',
					colors: '#666', // Optional: Style the y-axis numbers
				},
			},
			axisBorder: {
				show: false, // Remove the y-axis border
			},
			axisTicks: {
				show: false, // Remove y-axis ticks
			},
		},
		colors: ['#1e90ff'], // Custom bar colors
		grid: {
			show: false, // Remove grid lines
			padding: {
				top: -20, // Adjust the top padding
				right: 0,
				bottom: -5,
				left: 0,
			},
		},
		dataLabels: {
			enabled: true, // Show numbers on bars
			style: {
				colors: ['#ffffff'],
				fontSize: '14px',
				fontWeight: 'bold',
			},
		},
	};

	const chartSeries = [
		{
			name: 'Guess Distribution',
			data: stats.guessDistribution || [0, 0, 0, 0, 0, 0], // Default values if undefined
		},
	];

	return (
		<div className="stats-page">
			<Helmet>
				<title>Statistics - Movie Quotes Guesser</title>
				<link rel="canonical" href="https://www.moviequotesguesser.com/stats" />
				<meta
					name="description"
					content="Check out your Movie Quotes Guesser stats. See your wins, guess distribution, streaks, and more. Compete with friends and share your results!"
				/>
			</Helmet>
			{/* Header Section */}
			<div className="header-container">
				<div className="header-left">
					<SideMenu />
					<img
						src={triviaIcon}
						alt="Movie Quotes Guesser Trivia Icon"
						className="trivia-icon"
						onClick={() => navigate('/trivia')}
					/>
				</div>
				<div
					className="logo-box"
					onClick={() => {
						navigate('/');
					}}
				>
					<img src={logo} alt="Movie Quotes Guesser Logo" className="logo" />
					<h3 className="logo-text">Movie Quotes Guesser</h3>
				</div>
				<div className="header-right">
					<img src={statsIcon} alt="Stats Icon" className="stats-icon" onClick={() => navigate('/stats')} />
				</div>
			</div>

			{/* Statistics Content */}
			<div className="stats-container">
				<div className="stats-title">Your Stats</div>

				<div className="stats-overview">
					<div className="stats-box">
						<span className="stats-label">Games Played</span>
						<span className="stats-value">{stats.gamesPlayed || 0}</span>
					</div>
					<div className="stats-box">
						<span className="stats-label">Won</span>
						<span className="stats-value">{stats.gamesWon || 0}</span>
					</div>
					<div className="stats-box">
						<span className="stats-label">Win %</span>
						<span className="stats-value">
							{stats.gamesPlayed ? Math.round((stats.gamesWon / stats.gamesPlayed) * 100) : 0}%
						</span>
					</div>
					<div className="stats-box">
						<span className="stats-label">Current Streak</span>
						<span className="stats-value">{stats.currentStreak || 0}</span>
					</div>
					<div className="stats-box">
						<span className="stats-label">Max Streak</span>
						<span className="stats-value">{stats.maxStreak || 0}</span>
					</div>
				</div>

				{/* Guess Distribution Chart */}
				<div className="guess-distribution">
					<h2 className="guess-title">Guess Distribution</h2>
					<ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={200} />
				</div>

				<div className="go-home-button-container">
					<button className="go-home-button" onClick={() => navigate('/')}>
						← Home
					</button>
				</div>
			</div>

			{/* Footer Section */}
			<div className="footer-container">
				<div className="share-buttons">
					<img src={xIcon} alt="Share us on X" className="share-image" onClick={handleTwitterShare} />
					<img
						src={facebookIcon}
						alt="Share us on Facebook"
						className="share-image"
						onClick={handleFacebookShare}
					/>
					<img src={linkIcon} alt="Copy our website link" className="share-image" onClick={handleCopyLink} />
					{copied && <span className="copied-text">Copied</span>}
				</div>
				<FooterLinks />

				<a
					href="https://buymeacoffee.com/moviequotesguesser"
					target="_blank"
					rel="noopener noreferrer"
					className="support-button"
				>
					<img src={coffeeIcon} alt="Buy Me A Coffee" className="support-image" />
				</a>
			</div>
		</div>
	);
};

export default StatsPage;
